import { useMemo, useState } from 'react';
import PlatformBadge from './PlatformBadge';
import SortHeader from './SortHeader';
import { groupMeta, groupLabel, formatCell, sortRows, flatColumnsFor } from '../campaignObjectives';

/**
 * Vista "Anuncios": todos los anuncios juntos, taggeados por grupo de objetivo,
 * ordenables por cualquier columna. Muestra ~20 filas y el resto con scroll
 * vertical (header sticky). Las columnas de resultado dependen del vertical.
 */
export default function CampaignAdsTable({ ads, vertical, onPreview }) {
  const columns = flatColumnsFor(vertical);
  const [sort, setSort] = useState({ key: 'spend', dir: 'desc' });
  const rows = useMemo(() => sortRows(ads, sort.key, sort.dir), [ads, sort]);
  const onSort = (key) =>
    setSort((s) => ({ key, dir: s.key === key && s.dir === 'desc' ? 'asc' : 'desc' }));

  return (
    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
      <div className="max-h-[52rem] overflow-auto">
        <table className="w-full min-w-[720px] border-collapse text-sm">
          <thead>
            <tr className="sticky top-0 z-10 border-b border-gray-200 bg-white text-xs uppercase tracking-wide text-gray-500">
              <SortHeader sortKey="ad_name" sort={sort} onSort={onSort} align="left">
                Anuncio
              </SortHeader>
              <SortHeader sortKey="campaign_name" sort={sort} onSort={onSort} align="left">
                Campaña
              </SortHeader>
              <SortHeader sortKey="objective_group" sort={sort} onSort={onSort} align="left">
                Objetivo
              </SortHeader>
              {columns.map((c) => (
                <SortHeader key={c.key} sortKey={c.key} sort={sort} onSort={onSort}>
                  {c.label}
                </SortHeader>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((ad) => (
              <tr
                key={`${ad.platform}-${ad.ad_id}`}
                className="border-b border-gray-100 last:border-0 hover:bg-gray-50"
              >
                <td className="px-3 py-2.5">
                  <div className="flex items-center gap-2.5">
                    <PlatformBadge platform={ad.platform} status={ad.status} />
                    <span className="text-gray-900">{ad.ad_name || ad.ad_id}</span>
                    {ad.creative_id && onPreview && (
                      <button
                        type="button"
                        onClick={() => onPreview(ad)}
                        className="text-xs font-medium text-indigo-600 hover:underline"
                      >
                        Ver
                      </button>
                    )}
                  </div>
                </td>
                <td className="px-3 py-2.5 text-gray-500">{ad.campaign_name || '—'}</td>
                <td className="px-3 py-2.5">
                  <span
                    className={`rounded-md px-2 py-0.5 text-xs font-medium ${groupMeta(ad.objective_group).pill}`}
                  >
                    {groupLabel(ad.objective_group)}
                  </span>
                </td>
                {columns.map((c) => (
                  <td key={c.key} className="px-3 py-2.5 text-right tabular-nums text-gray-900">
                    {formatCell(ad[c.key], c.format)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
